import { ProtobufHttpClient } from "@ftdr/http-utils";
import { useMemo, useCallback } from "react";
import { WalletJSClient, walletpb } from "@ftdr/wallet-js-client";
import { PaymentMethod } from "../../../models";
import { CustomTenantHttpClient } from "../../../utils";
import {
  GeneralStatus,
  GeneralStatusValue,
} from "../../../components/payment-processors/types";

export type UpdateDefaultPaymentMethodResponseType<T = unknown> =
  | {
      status?: GeneralStatus;
      SuccessResponse?: T;
      FailureResponse?: string;
    }
  | undefined;

export type ResponseErrorType = {
  lastError: {
    response: {
      data: Uint8Array;
    };
    message: string;
  };
  message: string;
};

export type UpdateDefaultPaymentMethodRequest = {
  contractID?: string | null;
  dataStore: walletpb.IDataStore;
  customerID?: string | null;
  propertyID?: string | null;
  sourceTypeCode?: string;
};

type UseWalletService = (
  requestData: UpdateDefaultPaymentMethodRequest
) => Promise<UpdateDefaultPaymentMethodResponseType<PaymentMethod>>;

export const useWalletService = (
  token?: string,
  baseURL?: string,
  walletJsClientPassThrough?: WalletJSClient,
  tenant?: string
): UseWalletService => {
  const walletJsClient: WalletJSClient = useMemo(() => {
    if (walletJsClientPassThrough) {
      return walletJsClientPassThrough;
    } else {
      const tokenHttpClient = new CustomTenantHttpClient(
        token ?? "",
        tenant || "ahs"
      );
      const protoBuffHttpClient = new ProtobufHttpClient(tokenHttpClient);
      return new WalletJSClient(baseURL ?? "", protoBuffHttpClient);
    }
  }, [walletJsClientPassThrough, token, tenant, baseURL]);

  const updateDefaultPaymentMethod = useCallback(
    async ({
      contractID,
      customerID,
      dataStore,
      sourceTypeCode,
      propertyID,
    }: UpdateDefaultPaymentMethodRequest): Promise<
      UpdateDefaultPaymentMethodResponseType<PaymentMethod>
    > => {
      try {
        const result = await walletJsClient.updateDefaultPaymentMethod({
          contractID: contractID,
          customerID: customerID,
          dataStore,
          sourceTypeCode,
          propertyID: propertyID,
        });
        if (
          result.status === 200 &&
          result.data &&
          result?.data?.paymentMethod
        ) {
          return {
            status: GeneralStatusValue.PAYMENT_SUCCESS_SET_DEFAULT_SUCCESS,
            SuccessResponse: result?.data?.paymentMethod,
          };
        } else {
          return {
            status: GeneralStatusValue.PAYMENT_SUCCESS_SET_DEFAULT_FAILED,
            FailureResponse: "There is no payment method data in response",
          };
        }
      } catch (error) {
        const typedError = error as ResponseErrorType;
        // @TODO Check if the error should not be handled anyhow on ui
        return {
          status: GeneralStatusValue.PAYMENT_SUCCESS_SET_DEFAULT_FAILED,
          FailureResponse: typedError?.message || "Wallet Global Error",
        };
      }
    },
    [walletJsClient]
  );

  return updateDefaultPaymentMethod;
};
